import React, { useState, useMemo, useEffect } from "react";
import {
  Search,
  Filter,
  Calendar,
  Tag,
  ChevronRight,
  Bell,
  Star,
  TrendingUp,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Users,
  Eye,
  Loader,
  Loader2,
} from "lucide-react";
import "./Offers.scss";
import { Breadcrumb } from "components";
import api from "components/api/api";
import toast from "react-hot-toast";
import { Download } from "react-feather";

const Offers = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedTag, setSelectedTag] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [offerReport, setOfferReport] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [viewingOffer, setViewingOffer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isMandatory, setIsMandatory] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [allExportLoading, setAllExportLoading] = useState(false);
  const [singleExportLoading, setSingleExportLoading] = useState(false);
  const [offersLoading, setOffersLoading] = useState(true);

  const userRole = (localStorage.getItem("userRole") || "").toLowerCase();
  const userId = localStorage.getItem("userId") || "";

  const [regionData, setRegionData] = useState({});
  const [regionNames, setRegionNames] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState("All Regions");
  const [selectedDistrict, setSelectedDistrict] = useState("All Outlet");
  const [districts, setDistricts] = useState([]);
  const [userList, setUserList] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedOfferForReport, setSelectedOfferForReport] = useState(null);
  const [offers, setOffers] = useState([]);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "Offer & Plans",
    tags: "",
    priority: "medium",
    validUntil: "",
    discount: "",
    region: "All Regions",
    targetAudience: "All Users",
    status: "active",
  });

  const [offerType, setOfferType] = useState("TEXT_ONLY");
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");
  const [imageUploading, setImageUploading] = useState(false);

  const uploadOfferImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await api.uploadOfferImage(formData);
    return res.data.imageUrl;
  };

  const categories = [
    "all",
    "Offer & Plans",
    "Announcements",
    "Important Updates",
    "Management Directions",
    "New Product/Service",
    "Enhancement",
  ];

  const allexportEngagementReport = async () => {
    try {
      setAllExportLoading(true);
      const res = await api.downloadAllOfferViewReportExcel();
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Overall-offers-view-report.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to export report");
    } finally {
      setAllExportLoading(false);
    }
  };

  const exportEngagementReport = async () => {
    if (offerReport.length === 0) {
      toast.error("No data to export");
      return;
    }
    try {
      setSingleExportLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const headers = [
        "Staff Id",
        "Learner Name",
        "Offer Title",
        "Viewed",
        "Viewed Date",
        "Viewed Time",
      ];
      const rows = offerReport.map((row) => {
        if (!row.viewedAt) {
          return [
            row.staffId,
            `"${row.userName}"`,
            `${selectedOfferForReport.title}`,
            row.viewed ? "Yes" : "No",
            "Not Viewed",
            "",
          ];
        }
        const d = new Date(row.viewedAt);
        return [
          row.staffId,
          `"${row.userName}"`,
          `${selectedOfferForReport.title}`,
          "Yes",
          `"${d.toLocaleDateString()}"`,
          `"${d.toLocaleTimeString()}"`,
        ];
      });
      const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join(
        "\n",
      );
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `per-offer-view-report-${selectedOfferForReport.id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setSingleExportLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.fetchUsers();
      const users = res.data?.data || [];
      const filteredUsers = users.filter((user) => user.role !== "ADMIN");
      const regionData = { "All Regions": {} };
      filteredUsers.forEach((user) => {
        const { id, username, region, outlet } = user;
        if (!region || !outlet || !username) return;
        if (!regionData[region]) regionData[region] = {};
        if (!regionData[region][outlet]) regionData[region][outlet] = [];
        regionData[region][outlet].push({ id, username });
      });
      return { regionData };
    } catch (error) {
      console.error("Error fetching users", error);
      toast.error("Failed to fetch users");
      return null;
    }
  };

  const handleOfferCardClick = async (offer) => {
    if (!isAdmin) return;
    setSelectedOfferForReport(offer);
    setReportLoading(true);
    setOfferReport([]);
    try {
      const res = await api.fetchOfferViewReport(offer.id);
      setOfferReport(res.data?.data || []);
    } catch (e) {
      toast.error("Failed to load offer view report");
    } finally {
      setReportLoading(false);
    }
  };

  const fetchOffers = async () => {
    setOffersLoading(true);
    try {
      let response;
      if (userRole === "admin") {
        response = await api.fetchAllOffers();
      } else {
        response = await api.fetchOffersForUser(userId, userRole);
      }
      const fetchedOffers = response.data?.data || response.data || [];
      setOffers(fetchedOffers);
    } catch (error) {
      console.error("Error fetching offers:", error);
      toast.error("Failed to fetch offers");
    } finally {
      setOffersLoading(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      const data = await fetchUsers();
      if (data) {
        setRegionData(data.regionData);
        setRegionNames(Object.keys(data.regionData));
      }
      await fetchOffers();
    };
    initialize();
  }, []);

  useEffect(() => {
    if (Object.keys(regionData).length === 0) return;
    const allDistricts = Object.values(regionData).flatMap((region) =>
      Object.keys(region || {}),
    );
    const allUsers = Object.values(regionData)
      .flatMap((region) => Object.values(region || {}))
      .flat();
    setDistricts(allDistricts);
    setSelectedDistrict("All Outlet");
    setUserList(allUsers);
    setSelectedUsers(allUsers.map((u) => u.id));
  }, [regionData]);

  const handleRegionChange = (region) => {
    setSelectedRegion(region);
    setSelectedUsers([]);
    if (region === "All Regions") {
      const allDistricts = Object.values(regionData).flatMap((regionObj) =>
        Object.keys(regionObj || {}),
      );
      const allUsers = Object.values(regionData)
        .flatMap((regionObj) => Object.values(regionObj || {}))
        .flat();
      setDistricts(allDistricts);
      setSelectedDistrict("All Outlet");
      setUserList(allUsers);
      setSelectedUsers(allUsers.map((u) => u.id));
    } else {
      const regionDistricts = Object.keys(regionData[region] || {});
      const allUsersInRegion = Object.values(regionData[region] || {}).flat();
      setDistricts(regionDistricts);
      setSelectedDistrict("All Outlet");
      setUserList(allUsersInRegion);
      setSelectedUsers(allUsersInRegion.map((u) => u.id));
    }
  };

  const handleDistrictChange = (district) => {
    setSelectedDistrict(district);
    if (district === "All Outlet") {
      if (selectedRegion === "All Regions") {
        const allUsers = Object.values(regionData)
          .flatMap((regionObj) => Object.values(regionObj || {}))
          .flat();
        setUserList(allUsers);
        setSelectedUsers(allUsers.map((u) => u.id));
      } else {
        const users = Object.values(regionData[selectedRegion] || {}).flat();
        setUserList(users);
        setSelectedUsers(users.map((u) => u.id));
      }
    } else {
      if (selectedRegion === "All Regions") {
        const usersInDistrict = Object.values(regionData)
          .flatMap((regionObj) => regionObj[district] || [])
          .flat();
        setUserList(usersInDistrict);
        setSelectedUsers(usersInDistrict.map((u) => u.id));
      } else {
        const users = regionData[selectedRegion]?.[district] || [];
        setUserList(users);
        setSelectedUsers(users.map((u) => u.id));
      }
    }
  };

  const handleUserChange = (e) => {
    const { value, checked } = e.target;
    const id = value;
    if (value === "ALL") {
      setSelectedUsers(checked ? userList.map((u) => u.id) : []);
    } else {
      setSelectedUsers((prev) =>
        checked ? [...prev, id] : prev.filter((uid) => uid !== id),
      );
    }
  };

  const allTags = useMemo(() => {
    const tags = new Set(["all"]);
    offers.forEach((offer) => {
      if (offer.tags && Array.isArray(offer.tags)) {
        offer.tags.forEach((tag) => tags.add(tag));
      }
    });
    return Array.from(tags);
  }, [offers]);

  const filteredOffers = useMemo(() => {
    return offers.filter((offer) => {
      const matchesSearch =
        offer.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        offer.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (offer.tags &&
          offer.tags.some((tag) =>
            tag.toLowerCase().includes(searchQuery.toLowerCase()),
          ));
      const matchesCategory =
        selectedCategory === "all" || offer.category === selectedCategory;
      const matchesTag =
        selectedTag === "all" ||
        (offer.tags && offer.tags.includes(selectedTag));
      return matchesSearch && matchesCategory && matchesTag;
    });
  }, [searchQuery, selectedCategory, selectedTag, offers]);

  const handleCreateOffer = async () => {
    try {
      setLoading(true);
      let imageUrl = "";
      if (offerType !== "TEXT_ONLY") {
        if (!selectedImage) {
          toast.error("Image is required for this offer type");
          return;
        }
        setImageUploading(true);
        imageUrl = await uploadOfferImage(selectedImage);
        setUploadedImageUrl(imageUrl);
        setImageUploading(false);
      }
      const payload = {
        type: offerType,
        title: offerType !== "IMAGE_ONLY" ? formData.title : null,
        description: offerType !== "IMAGE_ONLY" ? formData.description : null,
        imageUrl: imageUrl || null,
        category: formData.category,
        tags: formData.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        priority: formData.priority,
        validUntil: formData.validUntil || null,
        discount: formData.discount || null,
        region: selectedRegion,
        targetUsers: selectedUsers,
        status: formData.status,
        isMandatory: isMandatory,
      };
      await api.createOffer(payload);
      toast.success("Offer created successfully");
      setShowCreateModal(false);
      resetForm();
      await fetchOffers();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to create offer");
    } finally {
      setLoading(false);
      setImageUploading(false);
    }
  };

  const handleUpdateOffer = async () => {
    if (!formData.title || !formData.description) {
      toast.error("Title and description are required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        tags: formData.tags
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t),
        priority: formData.priority,
        validUntil: formData.validUntil || null,
        discount: formData.discount || null,
        region: selectedRegion,
        targetAudience: formData.targetAudience,
        status: formData.status,
        targetedUsers: selectedUsers,
        isMandatory: isMandatory,
      };
      await api.updateOffer(editingOffer.id, payload);
      toast.success("Offer updated successfully!");
      setEditingOffer(null);
      resetForm();
      await fetchOffers();
    } catch (error) {
      console.error("Error updating offer:", error);
      toast.error(error.response?.data?.message || "Failed to update offer");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOffer = async (id) => {
    if (!window.confirm("Are you sure you want to delete this offer?")) return;
    try {
      await api.deleteOffer(id);
      toast.success("Offer deleted successfully!");
      await fetchOffers();
    } catch (error) {
      console.error("Error deleting offer:", error);
      toast.error(error.response?.data?.message || "Failed to delete offer");
    }
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setOfferType(offer.type || "TEXT_ONLY");
    setUploadedImageUrl(offer.imageUrl || "");
    setSelectedImage(null);
    setFormData({
      title: offer.title || "",
      description: offer.description || "",
      category: offer.category || "Offer & Plans",
      tags: offer.tags ? offer.tags.join(", ") : "",
      priority: offer.priority || "medium",
      validUntil: offer.validUntil || "",
      discount: offer.discount || "",
      region: offer.region || "All Regions",
      targetAudience: offer.targetAudience || "All Users",
      status: offer.status || "active",
    });
    setIsMandatory(offer.isMandatory || false);
    if (offer.region) handleRegionChange(offer.region);
    if (offer.targetedUsers && offer.targetedUsers.length > 0) {
      setSelectedUsers(offer.targetedUsers);
    }
  };

  const resetForm = () => {
    setOfferType("TEXT_ONLY");
    setSelectedImage(null);
    setUploadedImageUrl("");
    setImageUploading(false);
    setFormData({
      title: "",
      description: "",
      category: "Offer & Plans",
      tags: "",
      priority: "medium",
      validUntil: "",
      discount: "",
      region: "All Regions",
      targetAudience: "All Users",
      status: "active",
    });
    setSelectedRegion("All Regions");
    setSelectedDistrict("All Outlet");
    setIsMandatory(false);
    if (Object.keys(regionData).length > 0) {
      const allUsers = Object.values(regionData)
        .flatMap((region) => Object.values(region || {}))
        .flat();
      setUserList(allUsers);
      setSelectedUsers(allUsers.map((u) => u.id));
    }
  };

  const getPriorityClass = (priority) => `priority-${priority}`;

  const getCategoryIcon = (category) => {
    switch (category) {
      case "Offer & Plans":
        return <TrendingUp size={18} />;
      case "Announcements":
        return <Bell size={18} />;
      case "New Product/Service":
        return <Star size={18} />;
      case "Enhancement":
        return <ChevronRight size={18} />;
      default:
        return <Tag size={18} />;
    }
  };

  const isAdmin = userRole === "admin";

  if (offersLoading) {
    return (
      <div className="offers-container">
        <Breadcrumb title="Offers & Plans" />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
          }}
        >
          <p>loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="offers-container">
      <Breadcrumb title="Offers & Plans" />

      <div className="offers-header">
        <div className="header-content">
          <div className="header-title-row">
            <div>
              <p className="page-subtitle">
                {isAdmin
                  ? "Manage and create offers for your customers"
                  : "Discover exclusive deals"}
              </p>
            </div>
            {isAdmin && (
              <button
                className="create-btn"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus size={20} />
                Create New Offer
              </button>
            )}
          </div>
        </div>

        <div className="header-stats">
          <div className="stat-card">
            <span className="stat-value">{offers.length}</span>
            <span className="stat-label">Total Offers</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">
              {offers.filter((o) => o.priority === "high").length}
            </span>
            <span className="stat-label">Hot Deals</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">
              {
                offers.filter((o) => o.category === "New Product/Service")
                  .length
              }
            </span>
            <span className="stat-label">New Services</span>
          </div>
          {isAdmin && (
            <div className="stat-card">
              <span className="stat-value">
                {offers.filter((o) => o.status === "active").length}
              </span>
              <span className="stat-label">Active</span>
            </div>
          )}
        </div>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <Search className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Search offers, plans, services..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <div className="filter-label">
            <Filter size={16} />
            <span>Category</span>
          </div>
          <div className="filter-buttons">
            {categories.map((category) => (
              <button
                key={category}
                className={`filter-btn ${selectedCategory === category ? "active" : ""}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category === "all" ? "All" : category}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-group">
          <div className="filter-label">
            <Tag size={16} />
            <span>Tags</span>
          </div>
          <div className="filter-buttons">
            {allTags.slice(0, 8).map((tag) => (
              <button
                key={tag}
                className={`filter-btn tag-btn ${selectedTag === tag ? "active" : ""}`}
                onClick={() => setSelectedTag(tag)}
              >
                {tag === "all" ? "All Tags" : tag}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-action-cont">
          <button
            className="export-btn"
            onClick={() => {
              setSearchQuery("");
              setSelectedCategory("all");
              setSelectedTag("all");
            }}
          >
            Clear Filters
          </button>
          {isAdmin && (
            <button
              className="export-btn"
              onClick={allexportEngagementReport}
              disabled={allExportLoading}
            >
              {allExportLoading ? (
                <>
                  <Loader2 size={16} className="spinner-offer" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download size={16} />
                  Export Report
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* ── Offers Grid ── */}
      <div className="offers-grid">
        {filteredOffers.length > 0 ? (
          filteredOffers.map((offer) => (
            <div
              key={offer.id}
              // ✅ mandatory class added here
              className={`offer-card ${getPriorityClass(offer.priority)} ${offer.isMandatory ? "mandatory" : ""}`}
              onClick={() => handleOfferCardClick(offer)}
            >
              {isAdmin && (
                <div className="admin-actions">
                  <button
                    className="action-btn edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(offer);
                    }}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    className="action-btn delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteOffer(offer.id);
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}

              <div className="offer-header-badge">
                <div className="category-badge">
                  {getCategoryIcon(offer.category)}
                  <span>{offer.category}</span>
                </div>
                {offer.discount && (
                  <div className="discount-badge">{offer.discount}</div>
                )}
                {/* ✅ mandatory badge added here */}
                {offer.isMandatory && (
                  <span className="mandatory-badge">Mandatory</span>
                )}
              </div>

              {offer.imageUrl && (
                <img src={offer.imageUrl} alt="offer" className="offer-image" />
              )}

              {offer.type !== "IMAGE_ONLY" && (
                <>
                  <h3 className="offer-title">{offer.title}</h3>
                  <p className="offer-description">{offer.description}</p>
                </>
              )}

              {isAdmin && (
                <div className="admin-meta">
                  <div className="meta-item">
                    <Users size={14} />
                    <span>{offer.targetAudience}</span>
                  </div>
                  <div className="meta-item">
                    <Tag size={14} />
                    <span>{offer.region}</span>
                  </div>
                  <div className={`status-badge ${offer.status}`}>
                    {offer.status}
                  </div>
                </div>
              )}

              <div className="offer-tags">
                {offer.tags &&
                  offer.tags.map((tag, index) => (
                    <span key={index} className="tag">
                      {tag}
                    </span>
                  ))}
              </div>

              <div className="offer-footer">
                <div className="offer-meta">
                  <Calendar size={14} />
                  <span>
                    {offer.date
                      ? new Date(offer.date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : new Date(offer.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                  </span>
                </div>
                {offer.validUntil && (
                  <div className="valid-until">
                    Valid until{" "}
                    {new Date(offer.validUntil).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                )}
              </div>

              <button
                className="view-details-btn"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!isAdmin) {
                    try {
                      await api.markOfferAsViewed(offer.id, userId);
                    } catch (e) {
                      console.error("Failed to mark offer viewed", e);
                    }
                  }
                  setViewingOffer(offer);
                }}
              >
                <Eye size={18} />
                View Details
                <ChevronRight size={18} />
              </button>
            </div>
          ))
        ) : (
          <div className="no-results">
            <Search size={48} />
            <h3>No offers found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* ── Create / Edit Modal ── */}
      {(showCreateModal || editingOffer) && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowCreateModal(false);
            setEditingOffer(null);
            resetForm();
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-offer">
              <h2>{editingOffer ? "Edit Offer" : "Create New Offer"}</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingOffer(null);
                  resetForm();
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              <div className="region-user-section">
                <h3 className="section-title">Target Audience Selection</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Region *</label>
                    <select
                      value={selectedRegion}
                      onChange={(e) => handleRegionChange(e.target.value)}
                    >
                      {regionNames.map((region) => (
                        <option key={region}>{region}</option>
                      ))}
                    </select>
                  </div>
                  {districts.length > 0 && (
                    <div className="form-group">
                      <label>Outlet (District)</label>
                      <select
                        value={selectedDistrict}
                        onChange={(e) => handleDistrictChange(e.target.value)}
                        disabled={selectedRegion === "All Regions"}
                      >
                        <option value="All Outlet">All Outlet</option>
                        {districts.map((district) => (
                          <option key={district}>{district}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Select Users</label>
                  <div className="user-list">
                    <label className="user-checkbox">
                      <input
                        type="checkbox"
                        value="ALL"
                        checked={
                          userList.length > 0 &&
                          selectedUsers.length === userList.length
                        }
                        onChange={handleUserChange}
                      />
                      <span>Select All ({userList.length} users)</span>
                    </label>
                    <div className="user-scroll">
                      {userList.map((user) => (
                        <label key={user.id} className="user-checkbox">
                          <input
                            type="checkbox"
                            value={user.id}
                            checked={selectedUsers.includes(user.id)}
                            onChange={handleUserChange}
                          />
                          <span>{user.username}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {offerType !== "IMAGE_ONLY" && (
                <>
                  <div className="form-group full-width">
                    <label>Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Description *</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                    />
                  </div>
                </>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                  >
                    {categories
                      .filter((c) => c !== "all")
                      .map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Priority *</label>
                  <select
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({ ...formData, priority: e.target.value })
                    }
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Offer Type *</label>
                <select
                  value={offerType}
                  onChange={(e) => setOfferType(e.target.value)}
                >
                  <option value="TEXT_ONLY">Text Only</option>
                  <option value="IMAGE_ONLY">Image Only</option>
                  <option value="IMAGE_WITH_TEXT">Image + Text</option>
                </select>
              </div>

              {offerType !== "TEXT_ONLY" && (
                <div className="form-group">
                  <label>Offer Image *</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setSelectedImage(e.target.files[0])}
                  />
                  {imageUploading && <p>Uploading image...</p>}
                  {uploadedImageUrl && (
                    <img
                      src={uploadedImageUrl}
                      alt="Preview"
                      style={{ width: "100%", marginTop: 10 }}
                    />
                  )}
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Discount</label>
                  <input
                    type="text"
                    value={formData.discount}
                    onChange={(e) =>
                      setFormData({ ...formData, discount: e.target.value })
                    }
                    placeholder="e.g., 50% or Free"
                  />
                </div>
                <div className="form-group">
                  <label>Status *</label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label>Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) =>
                      setFormData({ ...formData, tags: e.target.value })
                    }
                    placeholder="e.g., Data, Premium, Limited"
                  />
                </div>
                {/* ✅ mandatory-toggle class applied */}
                <div className="form-group mandatory-toggle">
                  <input
                    type="checkbox"
                    id="mandatory-check"
                    checked={isMandatory}
                    onChange={(e) => setIsMandatory(e.target.checked)}
                  />
                  <label htmlFor="mandatory-check">Mandatory Offer</label>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingOffer(null);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button
                className="save-btn"
                onClick={editingOffer ? handleUpdateOffer : handleCreateOffer}
                disabled={
                  loading ||
                  (offerType !== "IMAGE_ONLY" &&
                    (!formData.title || !formData.description)) ||
                  (offerType !== "TEXT_ONLY" &&
                    !selectedImage &&
                    !uploadedImageUrl)
                }
              >
                {loading ? <p>loading...</p> : <Save size={18} />}
                {editingOffer ? "Update Offer" : "Create Offer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Details Modal ── */}
      {viewingOffer && (
        <div className="modal-overlay" onClick={() => setViewingOffer(null)}>
          <div
            className="modal-content view-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-offer">
              <h2>Offer Details</h2>
              <button
                className="close-btn"
                onClick={() => setViewingOffer(null)}
              >
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <div className="detail-header">
                  <div className="category-badge">
                    {getCategoryIcon(viewingOffer.category)}
                    <span>{viewingOffer.category}</span>
                  </div>
                  {viewingOffer.discount && (
                    <div className="discount-badge large">
                      {viewingOffer.discount}
                    </div>
                  )}
                  <div className={`priority-badge ${viewingOffer.priority}`}>
                    {viewingOffer.priority} priority
                  </div>
                </div>

                {viewingOffer.imageUrl && (
                  <img
                    src={viewingOffer.imageUrl}
                    alt="offer"
                    className="detail-image clickable"
                    onClick={() => setFullscreenImage(viewingOffer.imageUrl)}
                  />
                )}

                <h2 className="detail-title">{viewingOffer.title}</h2>
                <p className="detail-description">{viewingOffer.description}</p>

                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Category</label>
                    <span>{viewingOffer.category}</span>
                  </div>
                  <div className="detail-item">
                    <label>Priority</label>
                    <span className={`priority-text ${viewingOffer.priority}`}>
                      {viewingOffer.priority}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Status</label>
                    <span className={`status-badge ${viewingOffer.status}`}>
                      {viewingOffer.status}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Region</label>
                    <span>{viewingOffer.region || "All Regions"}</span>
                  </div>
                  <div className="detail-item">
                    <label>Target Audience</label>
                    <span>{viewingOffer.targetAudience || "All Users"}</span>
                  </div>
                  {viewingOffer.discount && (
                    <div className="detail-item">
                      <label>Discount</label>
                      <span className="discount-text">
                        {viewingOffer.discount}
                      </span>
                    </div>
                  )}
                  <div className="detail-item">
                    <label>Created Date</label>
                    <span>
                      {viewingOffer.date
                        ? new Date(viewingOffer.date).toLocaleDateString(
                            "en-US",
                            {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            },
                          )
                        : new Date(viewingOffer.createdAt).toLocaleDateString(
                            "en-US",
                            { month: "long", day: "numeric", year: "numeric" },
                          )}
                    </span>
                  </div>
                  {viewingOffer.validUntil && (
                    <div className="detail-item">
                      <label>Valid Until</label>
                      <span className="valid-date">
                        {new Date(viewingOffer.validUntil).toLocaleDateString(
                          "en-US",
                          { month: "long", day: "numeric", year: "numeric" },
                        )}
                      </span>
                    </div>
                  )}
                  {/* ✅ mandatory detail item inside the grid */}
                  <div
                    className={`detail-item ${viewingOffer.isMandatory ? "mandatory-item" : ""}`}
                  >
                    <label>Mandatory</label>
                    <span>
                      {viewingOffer.isMandatory
                        ? "Yes — Required viewing"
                        : "No"}
                    </span>
                  </div>
                </div>

                {viewingOffer.tags && viewingOffer.tags.length > 0 && (
                  <div className="detail-tags">
                    <label>Tags</label>
                    <div className="tags-list">
                      {viewingOffer.tags.map((tag, index) => (
                        <span key={index} className="tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {isAdmin &&
                  viewingOffer.targetedUsers &&
                  viewingOffer.targetedUsers.length > 0 && (
                    <div className="detail-item">
                      <label>Targeted Users</label>
                      <span>
                        {viewingOffer.targetedUsers.length} users selected
                      </span>
                    </div>
                  )}
              </div>
            </div>

            <div className="modal-footer">
              {isAdmin && (
                <button
                  className="edit-btn-modal"
                  onClick={() => {
                    handleEdit(viewingOffer);
                    setViewingOffer(null);
                  }}
                >
                  <Edit2 size={18} />
                  Edit Offer
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Offer View Report ── */}
      {isAdmin && selectedOfferForReport && (
        <div className="detail-section">
          <div className="section-right-actions">
            <h3 className="section-title">
              Offer View Report — {selectedOfferForReport.title}
            </h3>
            <button
              className="export-btn"
              onClick={exportEngagementReport}
              disabled={singleExportLoading}
            >
              {singleExportLoading ? (
                <>
                  <Loader2 size={18} className="spinner-offer" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download size={20} />
                  Export Report
                </>
              )}
            </button>
          </div>
          {reportLoading ? (
            <p>Loading report...</p>
          ) : offerReport.length === 0 ? (
            <p>No data available</p>
          ) : (
            <div className="report-list">
              {offerReport.map((row) => (
                <div className="report-row" key={row.staffId}>
                  <div className="report-user">
                    <span className="user-name">{row.userName}</span>
                    {row.viewedAt && (
                      <span className="viewed-at">
                        ( Viewed At &nbsp;
                        {new Date(row.viewedAt).toLocaleString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                        )
                      </span>
                    )}
                  </div>
                  <span
                    className={`report-status ${row.viewed ? "viewed" : "not-viewed"}`}
                  >
                    {row.viewed ? "Viewed" : "Not Viewed"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Fullscreen Image ── */}
      {fullscreenImage && (
        <div
          className="fullscreen-overlay"
          onClick={() => setFullscreenImage(null)}
        >
          <img src={fullscreenImage} alt="Full Screen" />
          <button
            className="fullscreen-close"
            onClick={() => setFullscreenImage(null)}
          >
            <X size={32} />
          </button>
        </div>
      )}
    </div>
  );
};

export default Offers;
